/* Yorm biometric quick-login wrapper.
 *
 * This module hides all Capacitor-specific calls behind a small async API so
 * the rest of the React app can stay platform-agnostic. Every helper is a
 * no-op (or returns a "not available" result) when the app is running in a
 * regular browser / PWA — biometrics are a native-only feature.
 *
 * Plugin: @capgo/capacitor-native-biometric (v5, paired with Capacitor 5).
 * Storage: credentials are persisted inside the iOS Keychain and Android
 * Keystore. We don't store anything sensitive in localStorage.
 *
 * Flow:
 *   1. After a successful email+password login, call enableBiometricLogin()
 *      to seal those credentials behind the device biometric prompt.
 *   2. On the next launch, hasStoredCredentials() tells the LoginPage whether
 *      to show the "Unlock with Face ID" button.
 *   3. unlockWithBiometric() triggers the native prompt; on success it
 *      returns {username, password} so the caller can replay the existing
 *      /api/auth/login endpoint and obtain a fresh JWT.
 *   4. clearBiometricCredentials() runs on explicit logout so the next
 *      person to use the device can't auto-login as the previous user.
 */
import { Capacitor } from "@capacitor/core";
import { NativeBiometric, BiometryType } from "@capgo/capacitor-native-biometric";

// Stable server key used by Keychain/Keystore to namespace our credentials.
// Treat it like a unique identifier — never change it after release or
// existing users will lose their stored credentials.
const SERVER_KEY = "app.yorm.quicklogin";

// localStorage flag so the LoginPage knows whether to render the
// "Unlock with Face ID" CTA without doing a Keychain round-trip on every
// render. The actual secret lives in the Keychain/Keystore.
const FLAG_KEY = "yorm_biometric_enabled";

const isNative = () => Capacitor.isNativePlatform();

/**
 * Returns { available, biometryType } describing the current device.
 * Always returns available=false on web / PWA so callers can hide the UI.
 */
export async function checkBiometricAvailability() {
  if (!isNative()) return { available: false, biometryType: null };
  try {
    const res = await NativeBiometric.isAvailable();
    return {
      available: !!res.isAvailable,
      // BiometryType enum: FACE_ID, TOUCH_ID, FACE_AUTHENTICATION, FINGERPRINT,
      // IRIS_AUTHENTICATION, MULTIPLE, NONE
      biometryType: res.biometryType ?? null,
    };
  } catch {
    return { available: false, biometryType: null };
  }
}

/**
 * Friendly label for the prompt button — "Face ID" on iPhone X+,
 * "Touch ID" on older iPhones, "Fingerprint" / "Face Unlock" on Android.
 */
export function biometryLabel(biometryType) {
  switch (biometryType) {
    case BiometryType.FACE_ID:
      return "Face ID";
    case BiometryType.TOUCH_ID:
      return "Touch ID";
    case BiometryType.FACE_AUTHENTICATION:
      return "Face Unlock";
    case BiometryType.FINGERPRINT:
      return "Fingerprint";
    case BiometryType.IRIS_AUTHENTICATION:
      return "Iris";
    case BiometryType.MULTIPLE:
      return "Biometrics";
    default:
      return "Biometrics";
  }
}

/** True if we've previously sealed credentials for this device. */
export function hasStoredCredentials() {
  if (!isNative()) return false;
  try {
    return localStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Seal {email, password} into the device Keychain/Keystore. Caller should
 * only invoke this AFTER a successful login and AFTER the user opts in to
 * biometric quick-login (don't enable silently — surprise prompts spook
 * users into uninstalling).
 */
export async function enableBiometricLogin(email, password) {
  if (!isNative()) return false;
  try {
    await NativeBiometric.setCredentials({
      username: email,
      password,
      server: SERVER_KEY,
    });
    localStorage.setItem(FLAG_KEY, "1");
    return true;
  } catch (err) {
    console.warn("Failed to enable biometric login:", err);
    return false;
  }
}

/**
 * Trigger the native biometric prompt. On success the device unlocks the
 * Keychain entry and returns the stored {username, password}. The caller
 * is responsible for re-submitting those to /api/auth/login.
 *
 * On user cancellation or biometric mismatch this throws — the LoginPage
 * already shows the password form so we just surface a soft toast and let
 * the user type their password.
 */
export async function unlockWithBiometric(reason = "Unlock Yorm with biometrics") {
  if (!isNative()) throw new Error("Biometric login is only available in the Yorm mobile app");
  // 1. Prompt for biometric verification — throws on cancel / mismatch.
  await NativeBiometric.verifyIdentity({
    reason,
    title: "Unlock Yorm",
    subtitle: "Use your saved credentials to log in",
    description: "Touch the fingerprint sensor or look at the camera",
  });
  // 2. Fetch the stored credentials. This will also throw if no credentials
  //    have been saved (caller should clear the flag and fall back to password).
  const creds = await NativeBiometric.getCredentials({ server: SERVER_KEY });
  return { email: creds.username, password: creds.password };
}

/**
 * Wipe stored credentials and disable the biometric prompt on this device.
 * Called from logout, "Disable biometric" toggles, and as a safety net when
 * unlockWithBiometric() reports no credentials present.
 */
export async function clearBiometricCredentials() {
  try {
    if (isNative()) {
      await NativeBiometric.deleteCredentials({ server: SERVER_KEY });
    }
  } catch {
    /* nothing to delete — that's fine */
  }
  try {
    localStorage.removeItem(FLAG_KEY);
  } catch {
    /* localStorage may be blocked in private mode */
  }
}

export { isNative };
